import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { SeletorNotaFiscalComponent } from '../../components/seletor-nota-fiscal/seletor-nota-fiscal.component';
import { VinculoNotaPedidoFluxoService } from '../../services/vinculo-nota-pedido-fluxo.service';

/** Abertura do modal: a compra que precisa de NF para entrar em Pedido Faturado. */
export class VincularNotaPedidoDialogData {
  idCompra: number;
  /** Código já formatado ("#24") para o título. */
  codigoPedido: string;
  tituloPedido: string | null = null;
  /** Etapa em que o pedido fica se o modal for cancelado (rodapé). */
  nomeEtapaOrigem: string;
}

/** Fechamento com o vínculo concluído (o vínculo já moveu o card). Cancelar fecha com `undefined`. */
export class VincularNotaPedidoDialogResult {
  vinculado = false;
}

/**
 * "Vincular nota fiscal ao pedido": aberto quando o pedido tenta entrar em Pedido Faturado sem NF. O
 * usuário escolhe a nota no seletor (notas do fornecedor, filtro na gaveta, XML ou lançamento manual) e a cadeia de vínculo existente roda a
 * partir daqui; concluída, o modal fecha com `vinculado: true`.
 */
@Component({
  selector: 'app-vincular-nota-pedido-dialog',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, MatIconModule, MatTooltipModule, SeletorNotaFiscalComponent],
  templateUrl: './vincular-nota-pedido-dialog.component.html',
  styleUrl: './vincular-nota-pedido-dialog.component.css',
})
export class VincularNotaPedidoDialogComponent {
  vinculando = false;
  isMaximizado = false;
  nomeFornecedor = '';

  constructor(
    public dialogRef: MatDialogRef<VincularNotaPedidoDialogComponent, VincularNotaPedidoDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: VincularNotaPedidoDialogData,
    private fluxo: VinculoNotaPedidoFluxoService,
    private alertService: AlertService,
  ) {}

  async vincular(idNFeEntrada: number): Promise<void> {
    if (this.vinculando) return;
    this.vinculando = true;
    try {
      const vinculado = await this.fluxo.executar(idNFeEntrada, () => ({ idsCompra: [this.data.idCompra], idsVinculoSubstituir: [] }));
      if (vinculado) {
        const result = new VincularNotaPedidoDialogResult();
        result.vinculado = true;
        this.dialogRef.close(result);
      }
    } catch (e) {
      this.alertService.show('Vincular nota fiscal', e instanceof Error ? e.message : 'Não foi possível vincular a nota ao pedido.', AlertType.error);
    } finally {
      this.vinculando = false;
    }
  }

  /** Abre em 96vw × 92vh; maximizar leva à tela cheia sem cantos, como o vínculo NF × pedido. */
  alternarTamanho(): void {
    this.isMaximizado = !this.isMaximizado;
    if (this.isMaximizado) {
      this.dialogRef.updateSize('100vw', '100vh');
      this.dialogRef.addPanelClass('associacao-dialog-maximizado');
    } else {
      this.dialogRef.updateSize('96vw', '92vh');
      this.dialogRef.removePanelClass('associacao-dialog-maximizado');
    }
    // O updateSize do CDK não reaplica a estratégia de posição: sem isto o modal restaurado vai para o canto.
    this.dialogRef.updatePosition();
    // Clique deixa o mat-icon-button com foco (e a sombra cinza); teclado continua com :focus-visible.
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  /** "Fornecedor · título do pedido", só com o que existir. */
  get subtitulo(): string {
    return [this.nomeFornecedor, this.data.tituloPedido].filter(Boolean).join(' · ');
  }

  cancelar(): void {
    if (this.vinculando) return;
    this.dialogRef.close(undefined);
  }
}
